import {Box} from '@mui/material';
import {t} from 'i18next';
import {ReactNode, useEffect, useState} from 'react';
import './ReviewTabs.scss';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as DownloadFilledIcon} from '../../../../assets/images/download-filled-icon.svg';
import CreateNewJob from '../create-job/CreateNewJob';
import ProtocolList from '../protocol-list/ProtocolList';
import ArrToastNotification from '../../../common/arr-toast-notification/ArrToastNotification';
import ArrLoadingSpinner from '../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {ToastType} from '../../../../shared/constants';
import {useExportAIReviewJobs} from '../../../../hooks/ai/review/useReview';
import {IAIReviewJobsExportRequest} from '../../../../api/types/aiReview.type';
import CreateNewProtocolModal from '../common/protocolIntelligenceV2/create-protocol-modal/CreateNewProtocolModal';
import {useLocation, useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {initializeNewProtocol} from '../../../../redux/slice/aiReviewSliceV2';
import {RootState} from '../../../../redux/store';
import {
  REVIEW_PROTOCOLS_TAB,
  ReviewProtocolsLocationState,
} from '../common/protocolIntelligenceV2/header/exit-protocol-modal/protocolExitNavigation';

export type ReviewTabType = 'jobs' | 'reviewProtocols';

type ReviewTabsProps = {
  jobsContent: ReactNode;
  hasJobsForExport?: boolean;
  jobExportPayload?: IAIReviewJobsExportRequest;
  isCreateJob: boolean;
  setIsCreateJob: React.Dispatch<React.SetStateAction<boolean>>;
  onTabChange?: (tab: ReviewTabType) => void;
};

type ReviewTabsToast = {
  isOpen: boolean;
  message: string;
  status: ToastType;
};

const reviewTabsToastDefaultValue: ReviewTabsToast = {
  isOpen: false,
  message: '',
  status: ToastType.Success,
};

const ReviewTabs: React.FC<ReviewTabsProps> = ({
  jobsContent,
  hasJobsForExport = false,
  jobExportPayload,
  isCreateJob,
  setIsCreateJob,
  onTabChange,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userRights = useSelector(
    (state: RootState) => state.actionmenu?.userRights
  );
  const canManageProtocols = Boolean(userRights?.canManageProtocols);
  const locationTab = (location.state as ReviewProtocolsLocationState | null)
    ?.tab;
  const [activeTab, setActiveTab] = useState<ReviewTabType>(
    locationTab === REVIEW_PROTOCOLS_TAB && canManageProtocols
      ? 'reviewProtocols'
      : 'jobs'
  );
  const [reviewTabsToast, setReviewTabsToast] = useState<ReviewTabsToast>(
    reviewTabsToastDefaultValue
  );
  const [isCreateProtocolModalOpen, setIsCreateProtocolModalOpen] =
    useState(false);

  const {mutateAsync: exportJobs, isPending: isExportingJobs} =
    useExportAIReviewJobs();

  useEffect(() => {
    if (locationTab === REVIEW_PROTOCOLS_TAB && canManageProtocols) {
      setActiveTab('reviewProtocols');
      onTabChange?.('reviewProtocols');
    }
  }, [locationTab, canManageProtocols, onTabChange]);

  useEffect(() => {
    if (!canManageProtocols && activeTab === 'reviewProtocols') {
      setActiveTab('jobs');
      onTabChange?.('jobs');
    }
  }, [canManageProtocols, activeTab, onTabChange]);

  const handleTabClick = (tab: ReviewTabType) => {
    setActiveTab(tab);
    onTabChange?.(tab);
  };

  const handleExportJobs = async () => {
    if (!hasJobsForExport || !jobExportPayload) return;
    try {
      await exportJobs(jobExportPayload);
      setReviewTabsToast({
        isOpen: true,
        status: ToastType.Success,
        message: t('ai.review.header.exportSuccess'),
      });
    } catch {
      setReviewTabsToast({
        isOpen: true,
        status: ToastType.Error,
        message: t('ai.review.header.exportFailed'),
      });
    }
  };

  const handleReviewTabsToastClose = () => {
    setReviewTabsToast(reviewTabsToastDefaultValue);
  };

  const handleCreateProtocol = () => {
    setIsCreateProtocolModalOpen(true);
  };

  const handleContinueProtocol = (protocolName: string) => {
    dispatch(initializeNewProtocol(protocolName));
    navigate('/AI/Review/ProtocolIntelligence', {state: protocolName});
    setIsCreateProtocolModalOpen(false);
  };

  const renderTabActions = () => {
    if (activeTab === 'jobs') {
      return (
        <>
          <ArrIconButton
            icon={
              <DownloadFilledIcon
                data-testid="download-icon"
                className={`download-filled-icon${!hasJobsForExport ? ' upload-icon-disabled' : ''}`}
                aria-hidden="true"
              />
            }
            ariaLabel={t('common.export')}
            className="upload-icon-button"
            testId="arr-icon-button"
            disabled={!hasJobsForExport || isExportingJobs}
            tabIndex={0}
            onClick={handleExportJobs}
          />
          <ArrButton
            variant="primary"
            size="small"
            disabled={false}
            aria-label={t('ai.review.tabs.createJob')}
            dataTestId="create-new-job"
            className="review-module-tabs-action-button"
            onClick={() => setIsCreateJob(true)}
          >
            {t('ai.review.tabs.createJob')}
          </ArrButton>
        </>
      );
    }

    return (
      <>
        <ArrButton
          variant="primary"
          size="small"
          disabled={false}
          aria-label={t('ai.review.tabs.createProtocol')}
          dataTestId="create-protocol"
          className="review-module-tabs-action-button"
          onClick={handleCreateProtocol}
        >
          {t('ai.review.tabs.createProtocol')}
        </ArrButton>
      </>
    );
  };

  return (
    <Box className="review-module-tabs">
      <Box className="review-module-tabs-bar">
        <Box className="review-module-tabs-list" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'jobs'}
            tabIndex={activeTab === 'jobs' ? 0 : -1}
            className={`review-module-tab typo-text subhead4_size ${activeTab === 'jobs' ? 'review-module-tab--active' : ''}`}
            onClick={() => handleTabClick('jobs')}
            data-testid="review-tab-jobs"
          >
            {t('ai.review.tabs.jobs')}
          </button>
          {canManageProtocols && (
            <button
              type="button"
              role="tab"
              aria-selected={activeTab === 'reviewProtocols'}
              tabIndex={activeTab === 'reviewProtocols' ? 0 : -1}
              className={`review-module-tab typo-text subhead4_size ${activeTab === 'reviewProtocols' ? 'review-module-tab--active' : ''}`}
              onClick={() => handleTabClick('reviewProtocols')}
              data-testid="review-tab-review-protocols"
            >
              {t('ai.review.tabs.reviewProtocols')}
            </button>
          )}
        </Box>
        <Box className="review-module-tabs-actions">{renderTabActions()}</Box>
      </Box>
      <Box className="review-module-tabs-panel" role="tabpanel">
        {activeTab === 'jobs' && jobsContent}
        {canManageProtocols && activeTab === 'reviewProtocols' && (
          <ProtocolList />
        )}
      </Box>
      {isCreateJob && (
        <CreateNewJob handleJobTypeModalClose={() => setIsCreateJob(false)} />
      )}
      {reviewTabsToast.isOpen && (
        <ArrToastNotification
          message={reviewTabsToast.message}
          onClose={handleReviewTabsToastClose}
          status={[reviewTabsToast.status]}
          data-testid="review-tabs-toast"
          aria-label={reviewTabsToast.message}
        />
      )}
      <ArrLoadingSpinner
        overlay
        isOpen={isExportingJobs}
        show={isExportingJobs}
        ariaLabel={t('common.loading')}
      />
      <CreateNewProtocolModal
        open={isCreateProtocolModalOpen}
        onClose={() => setIsCreateProtocolModalOpen(false)}
        onContinue={handleContinueProtocol}
      />
    </Box>
  );
};

export default ReviewTabs;
